import moment from 'moment';

// Parameter suffix constants - lowercase for run modal, uppercase for materialization
export const PARAM_SUFFIX = {
  START: '_start',
  END: '_end',
  ROLLING: '_rolling',
};

export const PARAM_SUFFIX_UPPER = {
  START: '_START',
  END: '_END',
  ROLLING: '_ROLLING',
};

// Data connection date parameter suffixes
export const DATA_CONNECTION_DATE_PARAMS = {
  START: '_data_connection_date_start',
  END: '_data_connection_date_end',
};

// Data type constants
export const DATA_TYPES = {
  DATE: 'DATE',
  TIMESTAMP: 'TIMESTAMP',
};

// Validation constants
export const VALIDATION = {
  ROLLING_DAYS: { MIN: 1, MAX: 999 },
  TTL_DAYS: { MIN: 1, MAX: 15 },
};

// Helper function to build parameter keys
export const getParamKey = (baseName, suffix) => `${baseName}${suffix}`;

// Helper function to extract base name from _rolling parameter
export const getBaseName = (paramName) => {
  const match = paramName.match(/^(.*)_rolling$/i);
  return match ? match[1] : null;
};

// Helper function to extract base name from any suffixed parameter
export const getBaseNameFromSuffix = (paramName, suffix) => {
  const regex = new RegExp(`${suffix}$`);
  return paramName.replace(regex, '');
};

// Helper function to check if data type is DATE or TIMESTAMP
export const isDateOrTimestampType = (dataType) => (
  dataType === DATA_TYPES.DATE || dataType === DATA_TYPES.TIMESTAMP
);

// Initialize rolling toggles from parameters
// Case-insensitive matching to handle both _ROLLING and _rolling
export const initializeRollingToggles = (parameters, suffix) => {
  const toggles = {};
  if (parameters) {
    parameters.forEach(param => {
      // Case-insensitive check for rolling suffix
      const rollingRegex = new RegExp(`${suffix.ROLLING}$`, 'i');
      if (rollingRegex.test(param.name)) {
        const baseName = param.name.replace(rollingRegex, '');
        toggles[baseName] = true;
      }
    });
  }
  return toggles;
};

// Create parameter map for quick lookup
export const createParameterMap = (parameters) => (
  (parameters || []).reduce((map, param) => ({
    ...map,
    [param.name]: param.value,
  }), {})
);

// Filter unique partition fields (one row per baseName for DATE/TIMESTAMP)
export const getUniquePartitionFields = (fields) => (
  fields.filter((field, index, self) => {
    const isDateTimeField = field.baseName && isDateOrTimestampType(field.dataType);
    return !isDateTimeField || index === self.findIndex(f => f.baseName === field.baseName);
  })
);

// Validate date range for start/end parameters
export const validateDateRange = (baseName, newParameters, suffix) => {
  const startParam = newParameters.find(p => p.name === `${baseName}${suffix.START}`);
  const endParam = newParameters.find(p => p.name === `${baseName}${suffix.END}`);

  const startValue = startParam?.value || '';
  const endValue = endParam?.value || '';

  // Error if END is filled but START is empty
  if (endValue && !startValue) {
    return 'Start date is required when end date is provided';
  }

  // Error if START is after END
  if (startValue && endValue) {
    const startDate = new Date(startValue);
    const endDate = new Date(endValue);

    if (startDate > endDate) {
      return 'Start date must be before end date';
    }
  }

  return null; // No error
};

// Helper function to validate rolling days input
export const validateRollingDays = (value) => {
  if (!value || value === '') return { isValid: true, message: '' };
  const num = parseInt(value, 10);
  const { MIN, MAX } = VALIDATION.ROLLING_DAYS;
  const isValid = !Number.isNaN(num) && num >= MIN && num <= MAX;
  return {
    isValid,
    message: isValid ? '' : `Enter ${MIN}-${MAX} days`,
  };
};

// Calculate date range from rolling days value
export const calculateRollingDateRange = (rollingDays) => {
  const days = parseInt(rollingDays, 10);
  if (Number.isNaN(days) || days <= 0) return null;

  // End date: end of today (23:59:59)
  const endDate = moment.utc().endOf('day');
  // Start date: beginning of the day, (N-1) days ago
  // "Last 15 days" = today + 14 previous days, starting at 00:00:00
  const startDate = moment.utc().subtract(days - 1, 'days').startOf('day');
  return { startDate, endDate };
};

// Format date based on data type (DATE vs TIMESTAMP)
export const formatDateByType = (date, dataType) => (
  dataType === DATA_TYPES.TIMESTAMP
    ? date.format('YYYY-MM-DDTHH:mm:ss')
    : date.format('YYYY-MM-DD')
);

// Auto-populate start/end dates from rolling values
export const autoPopulateFromRolling = (parameters, data) => {
  const updatedParameters = { ...parameters };
  const processedPrefixes = new Set();

  data.forEach((param) => {
    const baseName = getBaseName(param.name);
    if (!baseName || processedPrefixes.has(baseName)) return;
    if (!param.value || param.value === '') return;

    processedPrefixes.add(baseName);
    const startKey = `${baseName}${PARAM_SUFFIX.START}`;
    const endKey = `${baseName}${PARAM_SUFFIX.END}`;

    // Only auto-populate if both are empty
    if (updatedParameters[startKey] || updatedParameters[endKey]) return;

    // Find the corresponding start parameter to infer the date type (DATE vs TIMESTAMP)
    const startParam = data.find(p => p.name === startKey);
    const isValidDateType = startParam
      && [DATA_TYPES.DATE, DATA_TYPES.TIMESTAMP].includes(startParam.dataType);
    if (!isValidDateType) return;

    const dateRange = calculateRollingDateRange(param.value);
    if (dateRange) {
      // Use the start param's dataType to format the dates
      updatedParameters[startKey] = formatDateByType(dateRange.startDate, startParam.dataType);
      updatedParameters[endKey] = formatDateByType(dateRange.endDate, startParam.dataType);
    }
  });

  return updatedParameters;
};

